import { useLife } from '@/state/life';
import { usePower } from '@/state/power';
import { useXClone } from '@/state/xclone';
import { usePhone } from '@/state/phone';
import { useNotifications } from '@/state/notifications';
import { useCinematic } from '@/components/CinematicTakeover';
import { sounds } from '@/audio/synth';
import { seedFor } from './clock';
import { prngFromSeed } from './prng';

/**
 * Press Conference mini-arc.
 *
 * Triggered when notoriety crosses 60. Reporters DM you with 3 questions
 * over ~90 seconds. Each question has 3 CTA answers with distinct trade-offs
 * (tribal rep, notoriety, sanity, occasional cash). Answers compose into a
 * single closing X-Clone storm + a notoriety bump.
 */

interface Question {
  text: string;
  options: { label: string; effect: () => void; tribeShift?: string }[];
}

const QUESTIONS: Question[] = [
  {
    text: '“What do you say to retail traders who lost everything following your trades?”',
    options: [
      { label: '"They knew what they were getting into."', effect: () => {
        usePower.getState().bumpRep('wsb', 8);
        usePower.getState().bumpRep('press', -10);
        useLife.getState().bumpSanity(-3);
      }},
      { label: '"It was reckless of me. I\'ll do better."', effect: () => {
        usePower.getState().bumpRep('press', 10);
        usePower.getState().bumpRep('wsb', -8);
        useLife.getState().bumpSanity(4);
      }},
      { label: '"You\'re not real. I don\'t see your texts."', effect: () => {
        usePower.getState().bumpNotoriety(8);
        usePower.getState().bumpRep('press', -15);
        useLife.getState().bumpSanity(-6);
      }},
    ],
  },
  {
    text: '“Are you concerned about the SEC investigation?”',
    options: [
      { label: '"What investigation?"', effect: () => {
        useLife.getState().bumpSecHeat(8);
        usePower.getState().bumpNotoriety(5);
      }},
      { label: '"I\'m fully cooperating."', effect: () => {
        useLife.getState().bumpSecHeat(-4);
        usePower.getState().bumpRep('cnbc', 6);
      }},
      { label: '"My lawyer will be in touch with yours."', effect: () => {
        usePower.getState().bumpInfluence(5);
        useLife.getState().bumpSecHeat(2);
        usePower.getState().bumpRep('press', -5);
      }},
    ],
  },
  {
    text: '“If you had to do it all again, would you?”',
    options: [
      { label: '"Every time. The chart is my god."', effect: () => {
        usePower.getState().bumpRep('wsb', 12);
        usePower.getState().bumpRep('left', -10);
        usePower.getState().bumpNotoriety(6);
      }},
      { label: '"I\'d like to spend more time with my family."', effect: () => {
        useLife.getState().bumpRelationship('wife', 12);
        useLife.getState().bumpRelationship('mom', 8);
        usePower.getState().bumpRep('press', 5);
      }},
      { label: '"Buy $TARI."', effect: () => {
        usePower.getState().bumpRep('maga', 10);
        usePower.getState().bumpRep('press', -6);
        usePower.getState().bumpNotoriety(8);
      }},
    ],
  },
];

export function startPressConference(): void {
  // Cinematic intro
  useCinematic.getState().fire({
    id: `press-conf-${Date.now()}`,
    type: 'breaking',
    headline: 'PRESS CONFERENCE — LIVE NOW',
    subhead: 'three questions are coming. answer carefully. tribes are watching. so is your wife.',
    durationMs: 4000,
  });
  sounds.notifyAlert();

  // Schedule each question over the next ~90s
  for (let i = 0; i < QUESTIONS.length; i++) {
    const q = QUESTIONS[i]!;
    setTimeout(() => {
      // Generate stable button IDs from question index
      const actionIds = ['a', 'b', 'c'];
      const actions = q.options.map((opt, idx) => ({
        id: `${actionIds[idx]}`,
        label: opt.label,
        effectId: `press-conf-q${i}-${actionIds[idx]}`,
      }));
      // Register dynamic handlers (one-shot via in-memory map)
      registerOneShotHandlers(i, q);

      usePhone.setState((s) => ({
        ...s,
        contacts: s.contacts['press-pool'] ? s.contacts : { ...s.contacts, 'press-pool': { id: 'press-pool', displayName: 'Press Pool' } },
      }));

      usePhone.getState().receiveMessage({
        threadId: 'press-pool',
        fromNpc: 'press-pool',
        text: q.text,
        actions,
      });
      useNotifications.getState().push({
        surfaceId: 'phone',
        severity: 'urgent',
        text: `press conference: question ${i + 1}/3`,
      });
      sounds.notifyAlert();
    }, 5_000 + i * 30_000);
  }

  // Closing X-Clone storm after all questions
  setTimeout(() => {
    const seed = seedFor(`press-conf-close:${Date.now()}`, 0, 1);
    const rng = prngFromSeed(seed);
    const reactions = [
      'press conference take: he said something. analysts split.',
      'BREAKING: trader did press conference. 14 people watched. 3 of them were lawyers.',
      'the clip of his answer to question 2 is already a meme. you have not seen the meme. you will see the meme.',
      'media consensus: he was either deeply human or deeply unhinged. nobody can tell.',
    ];
    const text = reactions[Math.floor(rng() * reactions.length)] ?? '';
    useXClone.getState().receivePost({
      authorHandle: 'ReutersFlash',
      authorTribe: 'press',
      text,
      likes: 12000, reposts: 3400,
    });
    usePower.getState().bumpNotoriety(10);
  }, 5_000 + QUESTIONS.length * 30_000 + 5_000);
}

// One-shot handlers registered for the current press conference's questions
import { PHONE_ACTION_HANDLERS } from './phoneActions';

function registerOneShotHandlers(qIdx: number, q: Question): void {
  for (let i = 0; i < q.options.length; i++) {
    const id = `press-conf-q${qIdx}-${['a', 'b', 'c'][i]}`;
    PHONE_ACTION_HANDLERS[id] = () => {
      q.options[i]!.effect();
      sounds.fill();
    };
  }
}
